<template>
	<SectionContainer sectionId="similar-content">
		<LazyHeadlinedTitleList
			v-if="displaySimilarTitles && modulesByType.TITLE_SIMILAR_TITLES?.length"
			:titles="modulesByType.TITLE_SIMILAR_TITLES"
			:title="title"
			:sponsoredAd="sponsoredAd"
			:additionalContexts="additionalContexts"
			impressionTrackingLabel="similartitles"
		>
			{{ $t('WEBAPP_SIMILARTITLES', { title: title.content.title }) }}
		</LazyHeadlinedTitleList>

		<LazyHeadlinedTitleList
			v-if="modulesByType.TITLE_COMING_SOON?.length"
			:titles="modulesByType.TITLE_COMING_SOON"
			:title="title"
			:sponsoredAd="sponsoredAd"
			:additionalContexts="additionalContexts"
			impressionTrackingLabel="upcoming"
			impressionTrackingProperty="similartitles_upcoming"
		>
			{{ curatedUpcomingHeadline }}
		</LazyHeadlinedTitleList>

		<LazyHeadlinedTitleList
			v-if="modulesByType.TITLE_SIMILAR_UPCOMING?.length"
			:titles="modulesByType.TITLE_SIMILAR_UPCOMING"
			:title="title"
			:sponsoredAd="sponsoredAd"
			:additionalContexts="additionalContexts"
			impressionTrackingLabel="upcoming_genre"
			impressionTrackingProperty="similartitles_upcoming_genre"
		>
			{{ similarUpcomingHeadline }}
		</LazyHeadlinedTitleList>

		<LazyHeadlinedTitleList
			v-if="modulesByType.TITLE_SIMILAR_FREE?.length"
			:titles="modulesByType.TITLE_SIMILAR_FREE"
			:title="title"
			:sponsoredAd="sponsoredAd"
			:additionalContexts="additionalContexts"
			impressionTrackingLabel="free"
			impressionTrackingProperty="similartitles_free"
		>
			{{
				$t('WEBAPP_INTERLINKING_SIMILAR_FREE_TITLES', {
					titleType: $t(objectTypeTranslation[titleObjectType]),
				})
			}}
		</LazyHeadlinedTitleList>

		<LazyHeadlinedTitleList
			v-if="modulesByType.TITLE_POPULAR_ACTOR_DIRECTOR?.length && hasHeadlineTranslation"
			:titles="modulesByType.TITLE_POPULAR_ACTOR_DIRECTOR"
			:title="title"
			:sponsoredAd="sponsoredAd"
			:additionalContexts="additionalContexts"
			impressionTrackingLabel="cast"
			impressionTrackingProperty="similartitles_director_actor"
		>
			{{ similarActorDirectorHeadline }}
		</LazyHeadlinedTitleList>

		<LazyHeadlinedTitleList
			v-if="modulesDirectorAndActor.length && hasHeadlineTranslation"
			:titles="modulesDirectorAndActor"
			:title="title"
			:sponsoredAd="sponsoredAd"
			:additionalContexts="additionalContexts"
			impressionTrackingLabel="cast"
			impressionTrackingProperty="similartitles_director_actor"
		>
			{{ similarActorDirectorHeadline }}
		</LazyHeadlinedTitleList>

		<LazyHeadlinedTitleList
			v-if="modulesByType.TITLE_SUBGENRE_HIDDEN_GEMS?.length && hasHeadlineTranslation"
			:titles="modulesByType.TITLE_SUBGENRE_HIDDEN_GEMS"
			:title="title"
			:sponsoredAd="sponsoredAd"
			:additionalContexts="additionalContexts"
			impressionTrackingLabel="subgenres"
			impressionTrackingProperty="similartitles_hidden_gems"
		>
			{{ similarSubgenreHeadline }}
		</LazyHeadlinedTitleList>
	</SectionContainer>
</template>

<script setup lang="ts">
import type { Genres } from '@/constants/types';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { GetNodeTitleDetailsQuery } from '@/pages/graphql/queries/GetNodeTitleDetails.query';

const LazyHeadlinedTitleList = defineLazyHydrationComponent(
	'visible',
	() => import('@/components/HeadlinedTitleList.vue')
);
import SectionContainer from '@/pages/titleDetailV1/components/SectionContainer.vue';

import { CreditRole, ObjectType, Module } from '@/@types/graphql-types';

import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';

import { computed } from 'vue';

const props = withDefaults(
	defineProps<{
		displaySimilarTitles: boolean;
		title: AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Movie' | 'Show' | 'Season'>;
		genresByShortName: Genres;
		minTitles?: number;
		sponsoredAd?: SponsoredAdFragment | undefined;
		additionalContexts?: SnowplowContext[];
		impressionTrackingLabel?: string;
		impressionTrackingProperty?: string; // @todo problems with enum types - ImpressionTrackingEvents | '';
	}>(),
	{
		displaySimilarTitles: true,
		minTitles: 1,
		sponsoredAd: undefined,
		additionalContexts: () => [],
		impressionTrackingLabel: '',
		impressionTrackingProperty: '',
	}
);

const { t } = useI18n();
const languageStore = storeToRefs(useLanguageStore());
const { title, titleObjectType, credits, directors } = useTitleDetailInfo(() => props.title);

const objectTypeTranslation = {
	[ObjectType.Movie]: 'WEBAPP_MOVIES',
	[ObjectType.Show]: 'WEBAPP_TVSHOWS',
};

const titleModules = computed(() => props.title?.titleModules || []);

const modulesByType = computed(() => {
	return (titleModules.value || []).filter(Boolean).reduce((root, item) => {
		root[item!.template.technicalName] = ((item!.content as any)?.titles || []).map((title: any) => title.node);
		return root;
	}, {} as Record<string, Module[]>);
});

const modulesDirectorAndActor = computed(() => {
	const directorTitles = modulesByType.value.TITLE_POPULAR_DIRECTOR || [];
	const actorTitles = modulesByType.value.TITLE_POPULAR_ACTOR || [];
	const combinedTitles = [...directorTitles, ...actorTitles];
	return Array.from(new Set(combinedTitles));
});

const curatedUpcomingHeadline = computed(() => {
	switch (props.title.__typename) {
		case 'Movie':
			return t('WEBAPP_UPCOMING_MOVIES_LIST');
		case 'Show':
		case 'Season':
			return t('WEBAPP_UPCOMING_SHOWS_LIST');
		default:
			return '';
	}
});

const firstTitleGenreShortName = computed(() => {
	return props.title.content.genres?.[0]?.shortName ?? null;
});
const similarUpcomingHeadline = computed(() => {
	if (!firstTitleGenreShortName.value) return t('WEBAPP_UPCOMING_TITLES_LIST');

	const genre = props.genresByShortName[firstTitleGenreShortName.value]?.translation;
	switch (props.title.__typename) {
		case 'Movie':
			return t('WEBAPP_UPCOMING_GENRE_MOVIES_LIST', { genre });
		case 'Show':
		case 'Season':
			return t('WEBAPP_UPCOMING_GENRE_SHOWS_LIST', { genre });
		default:
			return '';
	}
});

const director = computed(() => {
	return directors.value.find(Boolean);
});
const mainActor = computed(() => {
	return credits.value.find(credit => credit.role === CreditRole.Actor);
});
const similarActorDirectorHeadline = computed(() => {
	const actorName = mainActor.value?.name;
	const directorName = director.value?.name;
	const titleType = t(objectTypeTranslation[titleObjectType.value]);
	const from = [actorName, directorName].filter(Boolean).join(' & ');
	const params = { titleType, directorAndActor: from };
	return titleObjectType.value === 'MOVIE'
		? t(`WEBAPP_INTERLINKING_DIRECTOR_ACTOR_MOVIES`, params)
		: t(`WEBAPP_INTERLINKING_DIRECTOR_ACTOR_SHOWS`, params);
});

const similarSubgenres = computed(() => {
	return (
		// if we're on a season page, we need to go over the show.content.subgenres to get them subgenres
		(
			(props.title as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Season'>).show?.content.subgenres ||
			props.title.content.subgenres ||
			[]
		)
			.map(subgenre => subgenre.content)
			.filter(subgenre => subgenre.shortName)
	);
});
const similarSubgenreHeadline = computed(() => {
	const firstSubgenre = similarSubgenres.value[0] ?? null;
	if (!firstSubgenre) {
		return '';
	}
	return t('WEBAPP_INTERLINKING_SUBGENRE', { subgenre: firstSubgenre.name });
});

const languagesWithAllHeadings = ['en', 'de', 'fr', 'it', 'es', 'pt', 'tr', 'cs', 'pl', 'ar'];
const hasHeadlineTranslation = computed(() => {
	return languagesWithAllHeadings.includes(languageStore.language.value);
});
</script>
